<template>
  <Dialog 
    v-model:visible="isVisible" 
    modal 
    :closable="true"
    :draggable="false"
    class="image-cropper-modal"
    :style="{ width: '90vw', maxWidth: '800px', zIndex: 9999 }"
  >
    <template #header>
      <h3 class="text-lg font-semibold">
        Recortar Imagem - {{ deviceLabel }}
      </h3>
    </template>

    <div class="space-y-4">
      <!-- Dimensões sugeridas -->
      <div class="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
        <p class="text-sm text-blue-800 dark:text-blue-200">
          <strong>Dimensões recomendadas:</strong> {{ recommendedDimensions.width }}x{{ recommendedDimensions.height }}px
        </p>
      </div>

      <!-- Cropper área -->
      <div class="relative bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden" style="max-height: 500px;">
        <img 
          v-if="imageUrl"
          ref="cropperImage" 
          :src="imageUrl" 
          alt="Image to crop"
          style="max-width: 100%; display: block;"
        />
      </div>

      <!-- Controles -->
      <div class="flex gap-2 justify-center">
        <Button 
          icon="pi pi-refresh" 
          text 
          rounded
          @click="rotate(-90)"
          v-tooltip.top="'Girar esquerda'"
        />
        <Button 
          icon="pi pi-replay" 
          text 
          rounded
          @click="rotate(90)"
          v-tooltip.top="'Girar direita'"
        />
        <Button 
          icon="pi pi-search-plus" 
          text 
          rounded
          @click="zoom(0.1)"
          v-tooltip.top="'Zoom in'"
        />
        <Button 
          icon="pi pi-search-minus" 
          text 
          rounded
          @click="zoom(-0.1)"
          v-tooltip.top="'Zoom out'"
        />
        <Button 
          icon="pi pi-undo" 
          text 
          rounded
          @click="reset"
          v-tooltip.top="'Resetar'"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button 
          label="Cancelar" 
          severity="secondary" 
          @click="cancel"
        />
        <Button 
          label="Aplicar" 
          @click="apply"
          :loading="processing"
        />
      </div>
    </template>
  </Dialog>
</template>

<script>
import { ref, computed, watch, onUnmounted } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';

export default {
  name: 'ImageCropperModal',
  components: {
    Dialog,
    Button
  },
  props: {
    visible: {
      type: Boolean,
      required: true
    },
    imageUrl: {
      type: String,
      default: null
    },
    device: {
      type: String,
      required: true,
      validator: (value) => ['mobile', 'tablet', 'desktop'].includes(value)
    }
  },
  emits: ['update:visible', 'cropped'],
  setup(props, { emit }) {
    const cropperImage = ref(null);
    const cropper = ref(null);
    const processing = ref(false);

    const dimensions = {
      mobile: { width: 480, height: 854 },
      tablet: { width: 1024, height: 768 },
      desktop: { width: 1920, height: 1080 }
    };

    const isVisible = computed({
      get() {
        return props.visible;
      },
      set(value) {
        emit('update:visible', value);
      }
    });

    const deviceLabel = computed(() => {
      const labels = {
        mobile: 'Mobile',
        tablet: 'Tablet',
        desktop: 'Desktop'
      };
      return labels[props.device];
    });

    const recommendedDimensions = computed(() => {
      return dimensions[props.device];
    });

    const aspectRatio = computed(() => {
      const dims = recommendedDimensions.value;
      return dims.width / dims.height;
    });

    const initCropper = () => {
      if (cropperImage.value && props.imageUrl) {
        // Destroy previous instance if exists
        if (cropper.value) {
          cropper.value.destroy();
        }

        // Create new cropper instance
        cropper.value = new Cropper(cropperImage.value, {
          aspectRatio: aspectRatio.value,
          viewMode: 1,
          autoCropArea: 1,
          background: true,
          rotatable: true,
          scalable: true,
          zoomable: true,
          cropBoxMovable: true,
          cropBoxResizable: true,
          responsive: true,
          restore: false,
          checkCrossOrigin: false,
          checkOrientation: false,
          modal: true,
          guides: true,
          center: true,
          highlight: true,
          dragMode: 'move',
          toggleDragModeOnDblclick: false
        });
      }
    };

    const destroyCropper = () => {
      if (cropper.value) {
        cropper.value.destroy();
        cropper.value = null;
      }
    };

    const rotate = (degrees) => {
      if (cropper.value) {
        cropper.value.rotate(degrees);
      }
    };

    const zoom = (ratio) => {
      if (cropper.value) {
        cropper.value.zoom(ratio);
      }
    };

    const reset = () => {
      if (cropper.value) {
        cropper.value.reset();
      }
    };

    const cancel = () => {
      isVisible.value = false;
      destroyCropper();
    };

    const apply = async () => {
      if (!cropper.value) return;

      processing.value = true;

      try {
        const canvas = cropper.value.getCroppedCanvas({
          width: recommendedDimensions.value.width,
          height: recommendedDimensions.value.height,
          imageSmoothingEnabled: true,
          imageSmoothingQuality: 'high'
        });

        canvas.toBlob((blob) => {
          const file = new File([blob], `background-${props.device}.jpg`, {
            type: 'image/jpeg',
            lastModified: Date.now()
          });

          emit('cropped', {
            device: props.device,
            file: file,
            dataUrl: canvas.toDataURL('image/jpeg', 0.9)
          });

          isVisible.value = false;
          processing.value = false;
          destroyCropper();
        }, 'image/jpeg', 0.9);
      } catch (error) {
        console.error('Erro ao recortar imagem:', error);
        processing.value = false;
      }
    };

    // Watch for modal visibility and image URL changes
    watch([() => props.visible, () => props.imageUrl], ([newVisible, newImageUrl]) => {
      console.log('🔍 ImageCropperModal watch:', { 
        visible: newVisible, 
        hasImageUrl: !!newImageUrl,
        imageUrlLength: newImageUrl?.length,
        device: props.device
      });
      
      if (newVisible && newImageUrl) {
        // Wait for next tick to ensure DOM is updated
        setTimeout(() => {
          console.log('🎯 Inicializando cropper...');
          initCropper();
        }, 100);
      } else if (!newVisible) {
        destroyCropper();
      }
    });

    onUnmounted(() => {
      destroyCropper();
    });

    return {
      cropperImage,
      processing,
      isVisible,
      deviceLabel,
      recommendedDimensions,
      aspectRatio,
      rotate,
      zoom,
      reset,
      cancel,
      apply
    };
  }
};
</script>

<style scoped>
.image-cropper-modal :deep(.p-dialog-content) {
  padding: 1.5rem;
}

.image-cropper-modal :deep(.cropper-view-box),
.image-cropper-modal :deep(.cropper-face) {
  border-radius: 0;
}
</style>
